import { useState, useCallback } from 'react';
import ReactDOM from 'react-dom/client';
import { createLogger } from '../../../lib/logger';
import { RSVPResponseWithTable, PhysicalInvitationStats } from '../types';
import { MailingLabelPrintPage } from '../../MailingLabelPrintPage';

const log = createLogger('rsvp');

interface UsePhysicalInvitationsParams {
  rsvpResponses: RSVPResponseWithTable[];
  physicalInvitationStats: PhysicalInvitationStats;
  downloadCSV: (csvContent: string, filename: string) => void;
  setExportingData: (v: boolean) => void;
}

export function usePhysicalInvitations({
  rsvpResponses,
  physicalInvitationStats,
  downloadCSV,
  setExportingData,
}: UsePhysicalInvitationsParams) {
  const [selectedPhysicalInvitations, setSelectedPhysicalInvitations] = useState<Set<number>>(new Set());
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [showExportOptions, setShowExportOptions] = useState(false);

  const togglePhysicalInvitationSelection = useCallback((index: number) => {
    const newSelected = new Set(selectedPhysicalInvitations);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedPhysicalInvitations(newSelected);
  }, [selectedPhysicalInvitations]);

  const selectAllPhysicalInvitations = useCallback(() => {
    if (selectedPhysicalInvitations.size === physicalInvitationStats.addressList.length) {
      setSelectedPhysicalInvitations(new Set());
    } else {
      const allIndices = physicalInvitationStats.addressList.map((_: any, index: number) => index);
      setSelectedPhysicalInvitations(new Set(allIndices));
    }
  }, [selectedPhysicalInvitations, physicalInvitationStats]);

  const getPhysicalInvitationSelectAllState = useCallback(() => {
    const total = physicalInvitationStats.addressList.length;
    const selected = selectedPhysicalInvitations.size;
    if (selected === 0) return 'none';
    if (selected === total) return 'all';
    return 'partial';
  }, [selectedPhysicalInvitations, physicalInvitationStats]);

  const copyToClipboard = useCallback(async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (error) {
      log.error('複製失敗:', error);
    }
  }, []);

  const exportPhysicalInvitationsToCSV = useCallback(async (includePhone: boolean = false) => {
    try {
      setExportingData(true);

      const itemsToExport = selectedPhysicalInvitations.size > 0
        ? physicalInvitationStats.addressList.filter((_: any, index: number) => selectedPhysicalInvitations.has(index))
        : physicalInvitationStats.addressList;

      const csvHeaders = [
        '賓客姓名',
        '出席人數',
        '郵寄地址',
        ...(includePhone ? ['聯絡電話'] : []),
        '申請時間'
      ];

      const csvRows = await Promise.all(itemsToExport.map(async (item) => {
        let phoneNumber = '';
        if (includePhone) {
          const matchingRSVP = rsvpResponses.find(rsvp =>
            rsvp.guestName === item.guestName &&
            rsvp.rsvpData.attending === 'yes' &&
            rsvp.rsvpData.needsPhysicalInvitation
          );
          phoneNumber = matchingRSVP?.rsvpData.contactPhone || '';
        }

        return [
          item.guestName,
          item.guestCount.toString(),
          `"${item.postalAddress}"`,
          ...(includePhone ? [phoneNumber] : []),
          new Date().toLocaleDateString('zh-TW')
        ];
      }));

      const csvContent = [
        csvHeaders.join(','),
        ...csvRows.map(row => row.join(','))
      ].join('\n');

      const filename = selectedPhysicalInvitations.size > 0
        ? `實體喜帖寄送清單_選擇項目_${new Date().toISOString().split('T')[0]}.csv`
        : `實體喜帖寄送清單_${new Date().toISOString().split('T')[0]}.csv`;

      downloadCSV(csvContent, filename);
    } catch (error) {
      log.error('匯出失敗:', error);
    } finally {
      setExportingData(false);
    }
  }, [selectedPhysicalInvitations, physicalInvitationStats, rsvpResponses, downloadCSV, setExportingData]);

  const handleExportSelectedMailingLabels = useCallback(async (includePhone: boolean = false) => {
    if (selectedPhysicalInvitations.size === 0) {
      alert('請先選擇要匯出的項目。');
      return;
    }

    try {
      const selectedItems = physicalInvitationStats.addressList.filter((_: any, index: number) =>
        selectedPhysicalInvitations.has(index)
      );

      let enhancedItems = selectedItems;
      if (includePhone) {
        enhancedItems = await Promise.all(selectedItems.map(async (item) => {
          const matchingRSVP = rsvpResponses.find(rsvp =>
            rsvp.guestName === item.guestName &&
            rsvp.rsvpData.attending === 'yes' &&
            rsvp.rsvpData.needsPhysicalInvitation
          );

          return {
            ...item,
            phoneNumber: matchingRSVP?.rsvpData.contactPhone || ''
          };
        }));
      }

      const printWindow = window.open('', '_blank');
      if (printWindow) {
        const container = printWindow.document.createElement('div');
        printWindow.document.body.appendChild(container);

        const root = ReactDOM.createRoot(container);
        root.render(<MailingLabelPrintPage addresses={enhancedItems} includePhone={includePhone} />);

        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      } else {
        alert('無法開啟新視窗，請檢查您的瀏覽器設定。');
      }
    } catch (error) {
      log.error('匯出選中項目失敗:', error);
      alert('匯出失敗，請稍後再試。');
    }
  }, [selectedPhysicalInvitations, physicalInvitationStats, rsvpResponses]);

  return {
    selectedPhysicalInvitations,
    copiedIndex,
    showExportOptions,
    setShowExportOptions,
    togglePhysicalInvitationSelection,
    selectAllPhysicalInvitations,
    getPhysicalInvitationSelectAllState,
    copyToClipboard,
    exportPhysicalInvitationsToCSV,
    handleExportSelectedMailingLabels,
  };
}
